import axios from "axios";
import { endpoints } from "../_config";

// 150ms: long enough to collapse a typing burst, short enough that the
// dropdown feels immediate now that the backend answers in ~10ms
const DEBOUNCE_MS = 150;
const CACHE_TTL_MS = 60 * 1000;
const CACHE_MAX_ENTRIES = 200;

const cache = new Map();
let debounceTimer = null;
let waiters = [];

// Callers mutate the returned user objects in place (they swap id/userId for
// CKEditor), so every caller must get its own copy — sharing one reference
// corrupts the cache and every other pending caller.
const clone = (data) => JSON.parse(JSON.stringify(data));

class MentionService {
  /**
   * search username in community
   *
   * Debounced on the trailing edge: rapid keystrokes collapse into a single
   * HTTP call and every pending caller resolves with the LATEST query's
   * results, so a slow stale response can never overwrite a newer one.
   * Results are cached per query for a minute, so backspacing/retyping a
   * prefix answers instantly without a network call.
   * @param {*} user
   * @param {*} communityId
   * @param {*} query
   * @returns
   */
  static searchInCommunity(user, communityId, query) {
    const key = `${communityId}|${(query || "").trim().toLowerCase()}`;
    const hit = cache.get(key);
    if (hit && Date.now() - hit.ts < CACHE_TTL_MS) {
      return Promise.resolve(clone(hit.data));
    }

    return new Promise((resolve) => {
      waiters.push(resolve);
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        const pendingWaiters = waiters;
        waiters = [];
        axios
          .get(endpoints.mentions.searchInCommunity(communityId, query), {
            headers: {
              Authorization: `Bearer ${user.token}`,
            },
          })
          .then((response) => {
            if (cache.size >= CACHE_MAX_ENTRIES) {
              cache.delete(cache.keys().next().value);
            }
            cache.set(key, { ts: Date.now(), data: response.data });
            pendingWaiters.forEach((w) => w(clone(response.data)));
          })
          .catch(() => {
            // fail soft: an empty dropdown beats a crashed mention widget
            pendingWaiters.forEach((w) => w({ users: [] }));
          });
      }, DEBOUNCE_MS);
    });
  }
}

export { MentionService };
